import Button from 'devextreme-react/button';
import SelectBox from 'devextreme-react/select-box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, readJson } from '../api/client';
import { TipBodyView } from './TipBodyView';

export interface DashboardTipPublic {
  id: string;
  title: string;
  body: string;
  category: string;
  sortOrder: number;
}

export const TIPS_AND_TRICKS_CARD_ID = 'tipsAndTricks' as const;

export const TIPS_AND_TRICKS_CARD = {
  id: TIPS_AND_TRICKS_CARD_ID,
  title: 'Tips & Tricks'
} as const;

const SHOW_COUNT_OPTIONS = [1, 2, 3, 5] as const;

function prefsKey(username: string | null): string {
  return `jarvis-dashboard-tips-prefs:${encodeURIComponent(username ?? 'user')}`;
}

type TipsPrefs = {
  showCount: number;
  orderIds: string[];
};

function readPrefs(username: string | null): TipsPrefs {
  try {
    const raw = localStorage.getItem(prefsKey(username));
    if (!raw) return { showCount: 2, orderIds: [] };
    const parsed = JSON.parse(raw) as Partial<TipsPrefs>;
    const showCount = SHOW_COUNT_OPTIONS.includes(parsed.showCount as (typeof SHOW_COUNT_OPTIONS)[number])
      ? (parsed.showCount as number)
      : 2;
    return {
      showCount,
      orderIds: Array.isArray(parsed.orderIds) ? parsed.orderIds.filter((x) => typeof x === 'string') : []
    };
  } catch {
    return { showCount: 2, orderIds: [] };
  }
}

function writePrefs(username: string | null, prefs: TipsPrefs): void {
  try {
    localStorage.setItem(prefsKey(username), JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

function shuffleIds(ids: string[]): string[] {
  const next = [...ids];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function orderTips(tips: DashboardTipPublic[], orderIds: string[]): DashboardTipPublic[] {
  const byId = new Map(tips.map((t) => [t.id, t]));
  const ordered: DashboardTipPublic[] = [];
  for (const id of orderIds) {
    const tip = byId.get(id);
    if (tip) {
      ordered.push(tip);
      byId.delete(id);
    }
  }
  for (const tip of tips) {
    if (byId.has(tip.id)) ordered.push(tip);
  }
  return ordered;
}

export async function fetchDashboardTips(): Promise<DashboardTipPublic[]> {
  const res = await apiFetch('/api/dashboard/tips');
  if (!res.ok) throw new Error(`Failed to load tips (${res.status})`);
  return readJson<DashboardTipPublic[]>(res);
}

export interface TipsAndTricksCardProps {
  username: string | null;
  title?: string;
}

export function TipsAndTricksCard({ username, title = 'Tips & Tricks' }: TipsAndTricksCardProps) {
  const [tips, setTips] = useState<DashboardTipPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<TipsPrefs>(() => readPrefs(username));

  useEffect(() => {
    setPrefs(readPrefs(username));
  }, [username]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchDashboardTips();
      setTips(rows);
      setError(null);
      setPrefs((prev) => {
        const next = {
          ...prev,
          orderIds:
            prev.orderIds.length > 0
              ? prev.orderIds.filter((id) => rows.some((t) => t.id === id))
              : rows.map((t) => t.id)
        };
        writePrefs(username, next);
        return next;
      });
    } catch (e) {
      setTips([]);
      setError(e instanceof Error ? e.message : 'Failed to load tips');
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleTips = useMemo(() => {
    const ordered = orderTips(tips, prefs.orderIds);
    return ordered.slice(0, Math.max(1, prefs.showCount));
  }, [prefs.orderIds, prefs.showCount, tips]);

  const updatePrefs = (next: TipsPrefs) => {
    setPrefs(next);
    writePrefs(username, next);
  };

  const onShuffle = () => {
    updatePrefs({
      ...prefs,
      orderIds: shuffleIds(tips.map((t) => t.id))
    });
  };

  return (
    <section className="dashboard-card dashboard-card--tips">
      <div className="dashboard-card__header">
        <div>
          <h3 className="dashboard-card__title">{title}</h3>
          <p>Quick pointers — full help docs cover the rest.</p>
        </div>
        <div className="dashboard-card__tips-actions">
          <SelectBox
            items={[...SHOW_COUNT_OPTIONS]}
            value={prefs.showCount}
            width={72}
            hint="How many tips to show"
            onValueChanged={(e) => {
              const value = Number(e.value);
              if (!SHOW_COUNT_OPTIONS.includes(value as (typeof SHOW_COUNT_OPTIONS)[number])) return;
              updatePrefs({ ...prefs, showCount: value });
            }}
          />
          <Button
            icon="refresh"
            stylingMode="text"
            hint="Shuffle tips"
            onClick={onShuffle}
            disabled={tips.length === 0}
          />
        </div>
      </div>

      {loading ? (
        <p className="dashboard-empty">Loading tips…</p>
      ) : error ? (
        <p className="dashboard-empty">{error}</p>
      ) : visibleTips.length === 0 ? (
        <p className="dashboard-empty">No tips published yet. Check back soon.</p>
      ) : (
        <ul className="dashboard-tips-list">
          {visibleTips.map((tip) => (
            <li key={tip.id} className="dashboard-tips-list__item">
              {tip.category ? (
                <span className="dashboard-tips-list__category">{tip.category}</span>
              ) : null}
              <strong className="dashboard-tips-list__title">{tip.title}</strong>
              <TipBodyView className="dashboard-tips-list__body" markdown={tip.body} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
